import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage, Card, CardContent, Heading, Text } from '@shared/ui';
import { useHelpdeskRuntime } from '../model/runtime';
import type { PresentationThread as HelpdeskThread } from '../model/runtime';
import { HelpdeskAttachment } from './HelpdeskAttachment';
import { HelpdeskMessageBody } from './HelpdeskMessageBody';

interface HelpdeskThreadMessageProps {
  taskUuid: string;
  message: HelpdeskThread['task'];
  original?: boolean;
  own?: boolean;
  viewerProfileId?: string;
  action?: ReactNode;
}

export function HelpdeskThreadMessage({
  taskUuid,
  message,
  original = false,
  own,
  viewerProfileId,
  action,
}: HelpdeskThreadMessageProps) {
  const { t, i18n } = useTranslation();
  const runtime = useHelpdeskRuntime();
  const kind = 'kind' in message ? message.kind : null;
  const isOwn =
    !original && (own ?? Boolean(viewerProfileId && message.author?.id === viewerProfileId));
  const messageLabel = original
    ? t('helpdesk.message', 'Original message')
    : isOwn
      ? t('helpdesk.thread.yourMessage', 'Your message')
      : message.author
        ? t('helpdesk.thread.otherMessage', 'Message from another author')
        : t('helpdesk.thread.unknownMessage', 'Message from an unknown author');
  const signature =
    ('signature' in message ? message.signature : null) ??
    message.author?.displayName ??
    (original ? message.contactEmail : null) ??
    t('helpdesk.authorNotEstablished', 'Author not established');
  const date = message.createdAt
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(message.createdAt))
    : null;
  const kindLabel = kind ? t(`helpdesk.thread.kind.${kind}`, kind) : null;
  return (
    <article
      aria-label={messageLabel}
      className={`flex w-full ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <Card
        className={
          original
            ? 'w-full min-w-0 rounded-xl border-border bg-card'
            : `w-full min-w-0 max-w-[94%] rounded-xl sm:max-w-[85%] lg:max-w-[75%] ${kind === 'internal-comment' ? 'border-warning/40 bg-warning/10' : isOwn ? 'border-primary/20 bg-primary/10' : 'border-border bg-muted/50'}`
        }
      >
        <CardContent className="space-y-2 p-3 sm:p-4">
          {action ? <div className="flex justify-end">{action}</div> : null}
          {original ? (
            <Heading as="h2" size="sm" className="break-words">
              {t('helpdesk.message', 'Original message')}
            </Heading>
          ) : message.title ? (
            <Text size="sm" color="muted" className="break-words">
              {message.title}
            </Text>
          ) : null}
          {kindLabel ? (
            <Text size="sm" color="muted">
              {kindLabel}
            </Text>
          ) : null}
          <HelpdeskMessageBody description={message.description} />
          {runtime.audience === 'staff' && kind !== 'internal-comment' && message.emailDelivery ? (
            <Text size="sm" color="muted" role="status">
              {t(`helpdesk.emailDelivery.status.${message.emailDelivery.status}`)}
            </Text>
          ) : null}
          {message.files.map((file, index) => (
            <HelpdeskAttachment
              key={`${viewerProfileId ?? 'anonymous'}:${taskUuid}:${file.fileRef ?? String(index)}`}
              taskUuid={taskUuid}
              file={file}
            />
          ))}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            {message.author ? (
              <Avatar className="size-6 shrink-0" role="img" aria-label={signature}>
                {message.author.avatarUrl ? (
                  <AvatarImage src={message.author.avatarUrl} alt="" />
                ) : null}
                <AvatarFallback>{signature.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
            ) : null}
            <Text size="sm" color="muted" className="break-words text-right">
              {signature}
              {date ? ` (${date})` : ''}
            </Text>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}
